import type { ListDiscernDocumentsQuery } from 'generated/discern.generated';

import { formatName } from './format';

type DiscernDocument =
  ListDiscernDocumentsQuery['listDiscernDocuments'][number];

export interface DiscernRowData {
  patientName: string;
  filename: string;
  uploadedBy: string;
  dateUploaded: string;
  lastUpdated: string;
  status: string;
  canRetry: boolean;
  assignedUserId: string;
  assignedUserName: string;
  vitallPatient?: {
    user_id?: string | null;
    vitall_patient_id: string;
  } | null;
  discernDocumentId: string;
  s3_key: string;
  groupingStateJson: Record<string, any>;
}

// Infer status from job timestamps only when doc.status is not explicitly set.
// This preserves the distinction between InReview (set by auto_curation.controller
// when results are ready for review) and Complete (set explicitly, or job has end_time).
function resolveStatus(doc: DiscernDocument): string {
  let status = doc.status || 'New';
  if (status === 'New' && doc.job) {
    if (doc.job.end_time) {
      status = 'Complete';
    } else if (doc.job.start_time) {
      status = 'Structuring';
    } else {
      status = 'Preparation';
    }
  }
  return status;
}

export function transformDiscernDocument(
  doc: DiscernDocument,
  patientNameOverride?: string
): DiscernRowData {
  const filename =
    doc.grouping_state_json.filename ||
    `document_${doc.discern_document_id}.pdf`;

  const metadata = doc.grouping_state_json._metadata as
    | {
        uploaded_by_name?: string;
        uploaded_by_user_id?: string;
        assigned_user_name?: string | null;
        assigned_user_id?: string | null;
      }
    | undefined;

  const uploadedBy = metadata?.uploaded_by_name || 'Unknown User';
  const assignedUserId =
    metadata?.assigned_user_id || metadata?.uploaded_by_user_id || '';
  const assignedUserName = metadata?.assigned_user_name || uploadedBy;

  const user = doc.vitall_patient?.user;
  const patientName =
    patientNameOverride ??
    (formatName(user?.first_name, user?.last_name) || 'Unknown Patient');

  return {
    patientName,
    filename,
    uploadedBy,
    dateUploaded: doc.created_at,
    lastUpdated: doc.updated_at || doc.created_at,
    status: resolveStatus(doc),
    // Whether startAutoCuration would accept a retry. The server owns this:
    // besides Failed it also admits a Structuring job that has been running
    // past the stale window, and that threshold is backend config the client
    // can't see.
    canRetry: doc.can_retry,
    assignedUserId,
    assignedUserName,
    vitallPatient: doc.vitall_patient,
    discernDocumentId: doc.discern_document_id,
    s3_key: doc.binary_resource_id,
    groupingStateJson: doc.grouping_state_json,
  };
}

export function transformDiscernDocuments(
  docs: DiscernDocument[],
  patientNameOverride?: string
): DiscernRowData[] {
  return docs.map((doc) => transformDiscernDocument(doc, patientNameOverride));
}

export function buildAssignedUsersMap(
  docs: DiscernDocument[]
): Record<string, string> {
  return docs.reduce<Record<string, string>>((acc, curr) => {
    const metadata = curr.grouping_state_json._metadata as
      | {
          uploaded_by_name?: string;
          uploaded_by_user_id?: string;
          assigned_user_name?: string | null;
          assigned_user_id?: string | null;
        }
      | undefined;
    if (!metadata) return acc;
    const userId = metadata.assigned_user_id ?? metadata.uploaded_by_user_id;
    const userName = metadata.assigned_user_name ?? metadata.uploaded_by_name;
    if (!userId || userId in acc) return acc;
    if (userName) acc[userId] = userName;
    return acc;
  }, {});
}

/**
 * Build the startAutoCuration input for retrying a document, or null when the
 * row can't produce a valid one. The mutation requires a patient and rejects
 * an empty pageGroups, so both are checked here rather than letting the call
 * fail server-side. A retry resends the document's existing page groups: the
 * resolver detects the existing job and resets it in place.
 */
export function buildRetryAutoCurationInput(rowData: DiscernRowData): {
  binary_id: string;
  vitall_patient_id: string;
  pageGroups: number[][];
} | null {
  const vitall_patient_id = rowData.vitallPatient?.vitall_patient_id;
  const pageGroups = rowData.groupingStateJson.pageGroups as
    number[][] | undefined;
  if (!vitall_patient_id || !pageGroups?.length) {
    return null;
  }
  return { binary_id: rowData.s3_key, vitall_patient_id, pageGroups };
}

export function applyClientFilters(
  rows: DiscernRowData[],
  clientFilters: { uploader: string; status: string }
): DiscernRowData[] {
  let result = rows;
  if (clientFilters.uploader) {
    const lower = clientFilters.uploader.toLowerCase();
    result = result.filter((r) => r.uploadedBy.toLowerCase() === lower);
  }
  if (clientFilters.status) {
    result = result.filter((r) => r.status === clientFilters.status);
  }
  return result;
}
